import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`text-xl font-bold transition-colors ${scrolled ? 'text-gray-900' : 'text-white'}`}>南信州企画舎</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#about" className={`text-sm font-medium transition-colors hover:text-teal-600 ${scrolled ? 'text-gray-700' : 'text-white'}`}>About</a>
            <a href="#services" className={`text-sm font-medium transition-colors hover:text-teal-600 ${scrolled ? 'text-gray-700' : 'text-white'}`}>Services</a>
            <a href="#articles" className={`text-sm font-medium transition-colors hover:text-teal-600 ${scrolled ? 'text-gray-700' : 'text-white'}`}>Articles</a>
            <a href="#contact" className={`text-sm font-medium transition-colors hover:text-teal-600 ${scrolled ? 'text-gray-700' : 'text-white'}`}>Contact</a>
          </div>
          <button className="md:hidden">
            <i className={`ri-menu-line text-2xl ${scrolled ? 'text-gray-900' : 'text-white'}`}></i>
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://storage.readdy-site.link/project_files/50dc65d3-8b78-4d78-8088-0876899964f7/27d0fe78-7273-4a56-bb3a-ef8464a11a10_unnamed.png?v=3e234f410e635adfc970ec1bf2307113"
            alt="Minami-Shinshu landscape"
            className="w-full h-full object-cover object-center sm:object-top"
            style={{ objectPosition: 'center 30%' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        </div>
        
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto w-full pt-32 pb-12">
          <div className="mb-4 flex justify-center">
            <img 
              src="https://static.readdy.ai/image/55d08b6cc0db9fc23f3284d8e8019058/96e79992df975f205cee59796b587988.png"
              alt="南信州企画舎 Logo"
              className="w-28 sm:w-40 md:w-56 lg:w-64 h-auto"
            />
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-7xl font-bold mb-6 leading-tight">地域と産業をつなぐ<br/>企画・発信支援</h1>
          <p className="text-base sm:text-lg md:text-xl mb-8 text-gray-100 max-w-2xl mx-auto">Connecting regions and industries through planning and communication support</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#contact" className="inline-flex items-center justify-center px-8 py-4 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
              Get in Touch
            </a>
            <a href="#services" className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg font-semibold hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer border border-white/30">
              Learn More
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">今牧 親勇</h2>
              <p className="text-lg text-gray-600 mb-4 leading-relaxed">地域企画・広報コーディネーター</p>
              <p className="text-base text-gray-700 mb-6 leading-relaxed">
                南信州を拠点に、地域の魅力を発掘し、企画と発信を通じて「仕事」として形にしています。地域と産業をつなぎ、持続可能な未来を創造することを使命としています。
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg">
                    <i className="ri-map-pin-line text-teal-600 text-xl"></i>
                  </div>
                  <span className="text-gray-700">長野県飯田市</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg">
                    <i className="ri-phone-line text-teal-600 text-xl"></i>
                  </div>
                  <span className="text-gray-700">080-5506-2016</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg">
                    <i className="ri-mail-line text-teal-600 text-xl"></i>
                  </div>
                  <span className="text-gray-700">info.msl17@gmail.com</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg">
                    <i className="ri-global-line text-teal-600 text-xl"></i>
                  </div>
                  <a href="https://note.com/mackeypower" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700 cursor-pointer">note.com/mackeypower</a>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://assets.st-note.com/production/uploads/images/240107900/profile_a49a41b85e5d382ef6ae9bd643c76664.png?width=600"
                  alt="Chikao Imamaki"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Services</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">地域の魅力を最大限に引き出し、持続可能なビジネスへと導きます</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-xl mb-6">
                <i className="ri-lightbulb-line text-teal-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">地域企画</h3>
              <p className="text-gray-600 leading-relaxed">地域の特性を活かした企画立案から実行まで、トータルでサポートします。地域資源を最大限に活用し、新しい価値を創造します。</p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-xl mb-6">
                <i className="ri-megaphone-line text-teal-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">広報コーディネート</h3>
              <p className="text-gray-600 leading-relaxed">効果的な情報発信戦略の立案と実施。SNS、メディア対応、コンテンツ制作まで、包括的な広報活動をサポートします。</p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-xl mb-6">
                <i className="ri-links-line text-teal-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">地域連携支援</h3>
              <p className="text-gray-600 leading-relaxed">地域と企業、行政と民間など、様々なステークホルダーをつなぎ、協働プロジェクトの実現をサポートします。</p>
            </div>
          </div>
        </div>
      </section>

      {/* Definition Image Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <img
              src="https://static.readdy.ai/image/55d08b6cc0db9fc23f3284d8e8019058/7d58f9f1686e3cd670b2eb5d27070c14.png"
              alt="南信州企画舎 定義"
              className="w-full h-auto object-top"
            />
          </div>
        </div>
      </section>

      {/* Featured Work */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Featured Work</h2>
            <p className="text-lg text-gray-600">南信州の魅力を伝える取り組み</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="group cursor-pointer">
              <div className="aspect-video rounded-xl overflow-hidden mb-4 shadow-md">
                <img 
                  src="https://static.readdy.ai/image/55d08b6cc0db9fc23f3284d8e8019058/7eb377015b096635edf3257598c55b46.jpeg"
                  alt="Ichida Kaki"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">市田柿プロジェクト</h3>
              <p className="text-gray-600 leading-relaxed">南信州の風景の中で育つ市田柿。手をかけ、想いをつなぎ、未来へ。伝統的な干し柿作りの魅力を現代に伝える取り組み。</p>
            </div>

            <div className="group cursor-pointer">
              <div className="aspect-video rounded-xl overflow-hidden mb-4 shadow-md">
                <img 
                  src="https://assets.st-note.com/production/uploads/images/239528673/rectangle_large_type_2_6f77c4085669de6f5c9a9ffeec821267.jpg?width=800&dpr=2&frame=1"
                  alt="Regional Planning"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">地域企画・発信支援</h3>
              <p className="text-gray-600 leading-relaxed">地域の魅力を企画と発信で「仕事」にする。持続可能な地域づくりのための総合的なサポート。</p>
            </div>
          </div>
        </div>
      </section>

      {/* Articles Section */}
      <section id="articles" className="py-24 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Latest Articles</h2>
            <p className="text-lg text-gray-600">地域づくりに関する最新の記事</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <a href="https://note.com/mackeypower/n/na1d77054b2b6" target="_blank" rel="noopener noreferrer" className="group cursor-pointer">
              <div className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
                <div className="aspect-video overflow-hidden">
                  <img 
                    src="https://assets.st-note.com/production/uploads/images/244016745/rectangle_large_type_2_4601a6a7a0d34dcdd8d4c779331054f2.jpg?width=600&dpr=2&frame=1"
                    alt="Article"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-teal-600 transition-colors">南信州の風景の中で育つ市田柿。手をかけ、想いをつなぎ、未来へ。</h3>
                  <p className="text-sm text-gray-500">7時間前</p>
                </div>
              </div>
            </a>

            <a href="https://note.com/mackeypower/n/nd3c264e9d22a" target="_blank" rel="noopener noreferrer" className="group cursor-pointer">
              <div className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
                <div className="aspect-video overflow-hidden">
                  <img 
                    src="https://assets.st-note.com/production/uploads/images/242114507/rectangle_large_type_2_f49867b33b4978d7af38e5ed7b1bf7b9.jpg?width=600&dpr=2&frame=1"
                    alt="Article"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-teal-600 transition-colors">南信州企画舎、開業しました</h3>
                  <p className="text-sm text-gray-500">8日前</p>
                </div>
              </div>
            </a>

            <a href="https://note.com/mackeypower/n/ne8c65241a6a4" target="_blank" rel="noopener noreferrer" className="group cursor-pointer">
              <div className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
                <div className="aspect-video overflow-hidden">
                  <img 
                    src="https://assets.st-note.com/production/uploads/images/240557985/rectangle_large_type_2_003dfd5a54e9621b0298664fb6a511e2.jpg?width=600&dpr=2&frame=1"
                    alt="Article"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-teal-600 transition-colors">年の初めに</h3>
                  <p className="text-sm text-gray-500">2週間前</p>
                </div>
              </div>
            </a>
          </div>

          <div className="text-center mt-12">
            <a href="https://note.com/mackeypower" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-8 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
              View All Articles
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Get in Touch</h2>
            <p className="text-lg text-gray-600">お気軽にお問い合わせください</p>
          </div>

          <div className="bg-gray-50 rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-6">Contact Information</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg shrink-0">
                      <i className="ri-phone-line text-teal-600 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Phone</p>
                      <p className="text-gray-900 font-medium">080-5506-2016</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg shrink-0">
                      <i className="ri-mail-line text-teal-600 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Email</p>
                      <p className="text-gray-900 font-medium">info.msl17@gmail.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg shrink-0">
                      <i className="ri-map-pin-line text-teal-600 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Location</p>
                      <p className="text-gray-900 font-medium">長野県飯田市</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-6">Follow Me</h3>
                <div className="space-y-4">
                  <a href="https://note.com/mackeypower" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-4 bg-white rounded-lg hover:shadow-md transition-shadow cursor-pointer group">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg shrink-0 group-hover:bg-teal-600 transition-colors">
                      <i className="ri-article-line text-teal-600 text-xl group-hover:text-white transition-colors"></i>
                    </div>
                    <div>
                      <p className="text-gray-900 font-medium group-hover:text-teal-600 transition-colors">note</p>
                      <p className="text-sm text-gray-500">@mackeypower</p>
                    </div>
                  </a>
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-gray-200">
              <div className="aspect-video rounded-xl overflow-hidden shadow-md">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d207697.77326!2d137.82!3d35.51!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x601d1e8c3e3e3e3e%3A0x3e3e3e3e3e3e3e3e!2z6aOv55Sw5biC!5e0!3m2!1sja!2sjp!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Location Map"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4">南信州企画舎</h3>
              <p className="text-gray-400 text-sm leading-relaxed">地域企画・広報コーディネーター<br/>今牧 親勇 (Chikao Imamaki)</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#about" className="text-gray-400 hover:text-white transition-colors cursor-pointer">About</a></li>
                <li><a href="#services" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Services</a></li>
                <li><a href="#articles" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Articles</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Connect</h4>
              <div className="flex gap-3">
                <a href="https://note.com/mackeypower" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-lg hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-article-line text-xl"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-800 text-center">
            <p className="text-gray-400 text-sm">© 2026 南信州企画舎 Chikao Imamaki. All rights reserved. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}